import { useChat } from "@ai-sdk/react";
import {
  ActionIcon,
  Alert,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  TextInput
} from "@mantine/core";
import { IconPlayerStop, IconSend } from "@tabler/icons-react";
import { DefaultChatTransport } from "ai";
import classNames from "classnames";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject
} from "react";
import { useLocation } from "react-router";
import { neWebApi, getAuthHeaders } from "../../../api";
import { AlertPresets } from "../../../mantine/mantinePresets";
import { neWebUrls } from "../../../ne-web-urls";
import type { Rating } from "../../../types/neWebApi.types";
import urls from "../../../urls";
import { resolvePageContext } from "../pageContext/resolvers";
import "./ChatPanel.scss";
import { ChatMessage } from "./ChatMessage/ChatMessage";
import type { FeedbackState } from "./ChatMessage/MessageFeedback/MessageFeedback.types";
import { flattenConversationSnapshot } from "./flattenConversationSnapshot";

interface ChatPanelProps {
  inputRef?: RefObject<HTMLInputElement | null>;
  userpilotEnabled?: boolean;
}

function ChatPanel({ inputRef, userpilotEnabled = false }: ChatPanelProps) {
  const [input, setInput] = useState("");
  const [feedbackMap, setFeedbackMap] = useState(
    new Map<string, FeedbackState>()
  );
  const [stoppedMessageId, setStoppedMessageId] = useState<string | null>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const { error, messages, sendMessage, status, stop } = useChat({
    transport: new DefaultChatTransport({
      api: urls.api.aiChat(),
      credentials: "include",
      headers: getAuthHeaders
    })
  });

  const isBusy = status === "submitted" || status === "streaming";
  const isThinking = status === "submitted";

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      viewportRef.current?.scrollTo({
        top: viewportRef.current.scrollHeight,
        behavior: "smooth"
      });
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSubmit = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || isBusy) {
      return;
    }

    setInput("");
    sendMessage(
      { text: trimmed },
      { body: { page_context: resolvePageContext(pathname) } }
    );
  }, [input, isBusy, pathname, sendMessage]);

  const handleStop = useCallback(() => {
    if (isBusy) {
      const last = messages[messages.length - 1];
      const isInterruptedReply = last?.role === "assistant";
      if (isInterruptedReply) {
        setStoppedMessageId(last.id);
      }
    }

    stop();
  }, [isBusy, messages, stop]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
        return;
      }

      if (e.key === "Escape" && isBusy) {
        e.preventDefault();
        handleStop();
      }
    },
    [handleSubmit, handleStop, isBusy]
  );

  const handleFeedback = useCallback(
    async (messageId: string, rating: Rating, comment?: string) => {
      let previous: FeedbackState | undefined;

      setFeedbackMap((prev) => {
        previous = prev.get(messageId);
        const next = new Map(prev);
        next.set(messageId, { rating, comment: comment ?? null, error: false });
        return next;
      });

      try {
        await neWebApi.post(neWebUrls.api.aiChatFeedback(), {
          message_id: messageId,
          rating,
          comment: comment ?? null,
          conversation: flattenConversationSnapshot(messages, messageId)
        });
      } catch {
        setFeedbackMap((prev) => {
          const next = new Map(prev);
          next.set(messageId, {
            rating: previous?.rating ?? null,
            comment: previous?.comment ?? null,
            error: true
          });
          return next;
        });
      }
    },
    [messages]
  );

  return (
    <Stack className="ChatPanel" gap={0} h="100%">
      <ScrollArea
        className="ChatPanelMessages"
        flex={1}
        p="md"
        viewportRef={viewportRef}
      >
        <Stack gap="md">
          {messages.map((message, index) => {
            const isLastMessage = index === messages.length - 1;
            const isTerminal = !isLastMessage || !isBusy;

            return (
              <ChatMessage
                feedbackState={feedbackMap.get(message.id)}
                isAborted={message.id === stoppedMessageId}
                isTerminal={isTerminal}
                key={message.id}
                message={message}
                onFeedback={handleFeedback}
              />
            );
          })}
          {isThinking && (
            <Group gap="xs">
              <Loader size="xs" />
              <Text c="dimmed" size="xs">
                Einen Moment...
              </Text>
            </Group>
          )}
        </Stack>
      </ScrollArea>
      <Stack
        className={classNames("ChatPanelInputArea", {
          hasUserpilotLauncher: userpilotEnabled
        })}
        gap="xs"
      >
        {error ? (
          <Alert {...AlertPresets.error}>
            {error.message || "Etwas ist schiefgelaufen."}
          </Alert>
        ) : null}
        <TextInput
          placeholder="Fragen Sie nach Ihren Projekten, Standorten oder Erzeugern..."
          ref={inputRef}
          rightSection={
            isBusy ? (
              <ActionIcon
                aria-label="Anfrage stoppen"
                color="error"
                onClick={handleStop}
              >
                <IconPlayerStop size="1em" />
              </ActionIcon>
            ) : (
              <ActionIcon
                color="accent1.7"
                disabled={!input.trim()}
                onClick={handleSubmit}
              >
                <IconSend size="1em" />
              </ActionIcon>
            )
          }
          rightSectionWidth={40}
          value={input}
          onChange={(e) => setInput(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
        />
      </Stack>
    </Stack>
  );
}

export { ChatPanel };
